import { find, isEmpty } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import pluralize from 'pluralize';
import type { ReactElement } from 'react';

import { AccordionBlock } from '@/blocks/accordion-block';
import { ContentMapBlock } from '@/blocks/content-map-block';
import { FeaturesStatusBlock } from '@/blocks/features-status-block';
import { GridBlock } from '@/blocks/grid-block';
import { HeaderBlock } from '@/blocks/header-block';
import { NearbyPortsBlock } from '@/blocks/nearby-ports-block';
import { TableBlock } from '@/blocks/table-block';
import { MiniChartCard } from '@/components/cards/mini-chart-card';
import {
  formatNumbersForChart,
  getLastTwoMonthsDataSummary,
  getLastXMonths,
  NUMBER_VALUE_FORMATS,
} from '@/components/cards/mini-chart-card/utils';
import { IdentifierPanel } from '@/components/Identifier-panel';
import { PageLayout } from '@/components/layouts/page-layout';
import { RankingList } from '@/components/modules/ranking-list';
import { PageLoading } from '@/components/page-loading';
import { PAGE_REVALIDATE_INTERVAL_MONTH, RELIABILITY_CHART_KEYS, RESPONSIVE_IMAGE_WORLD_MAP } from '@/lib/constants';
import { colors } from '@/theme/constants';
import { PortPageInterface } from '@/types';
import { IdentifierApiType, NodeType } from '@/types/api-types';
import { ChartDatum } from '@/types/charts';
import { PostSnippets } from '@/types/cms-types';
import { cmsPostService } from '@/utils/api/services/cms.service/post';
import { portService } from '@/utils/api/services/ports.service';
import { formatPortTitle } from '@/utils/format/port-description';
import { generatePortFAQs } from '@/utils/frequently-asked-questions';
import { createMetaTagsWithCustomTitleAndDescription, createPortPageUrl } from '@/utils/helpers';
import { getOrdinalSuffix } from '@/utils/helpers/get-ordinal-suffix';
import { getFaqSchema } from '@/utils/helpers/json-ld';
import { transformNodesToNearbyLinks } from '@/utils/transform';
import { transformPortData } from '@/utils/transform/transform-port';

type PageProps = { port: PortPageInterface; posts: PostSnippets };

const Page = ({ port, posts }: PageProps): ReactElement => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <PageLoading />;
  }

  // Set up data needed for page
  const shouldShowPortRanking = !isEmpty(port.globalRankings) || !isEmpty(port.countryRankings);
  const portDetails = port.details;
  const portType = portDetails.type as NodeType;

  // Generate FAQ JSONLD object
  const faqJsonLd = getFaqSchema(port.faqs);

  const { hasIncreased, latestMonthText, secondToLastValue, lastValue, difference } = getLastTwoMonthsDataSummary({
    data: port.reliability?.arrivalOnTimePerformance as ChartDatum[],
    key: RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY,
  });

  const {
    hasIncreased: averageDaysHasIncreased,
    latestMonthText: averageDaysLatestMonthText,
    secondToLastValue: averageDaysSecondToLastValue,
    lastValue: averageDaysLastValue,
    difference: averageDaysDifference,
  } = getLastTwoMonthsDataSummary({
    data: port.reliability?.arrivalOnTimePerformance as ChartDatum[],
    key: RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE,
    dataType: NUMBER_VALUE_FORMATS.WHOLE_NUMBER,
    decimalPlaces: 1,
  });

  const transformedArrivalOnTimePerformance = formatNumbersForChart({
    data: port.reliability?.arrivalOnTimePerformance,
    key: RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY,
    multiplier: 100,
  });

  const transformedAverageDaysLate = formatNumbersForChart({
    data: port.reliability?.arrivalOnTimePerformance,
    key: RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE,
  });

  return (
    <PageLayout metaTags={port.seoMetaTags} canonical={port.canonical} headerVariant="dark" posts={posts}>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(faqJsonLd)}`,
          }}
        />
      </Head>
      <div className="blocks">
        <HeaderBlock
          id="page-header"
          heading={portDetails.name}
          headingElement="h1"
          body={`<p>${portDetails.description}</p>`}
          variant="route"
          image={RESPONSIVE_IMAGE_WORLD_MAP}
        >
          {portDetails.identifiers ? (
            <IdentifierPanel
              identifiers={portDetails.identifiers}
              type={portDetails.type}
              countryCode={portDetails.countryCode}
              screenReaderMsg="This Port is also identified by the following Port codes."
            />
          ) : null}
        </HeaderBlock>
        <ContentMapBlock id="content-map" data={port} />
        {!isEmpty(port.nearbyPorts) && (
          <NearbyPortsBlock
            id="nearby-ports"
            portName={portDetails.name}
            portType={portDetails.type}
            portCountry={portDetails.countryCode}
            links={transformNodesToNearbyLinks(port.nearbyPorts)}
            noTopPadding
            className="bg-grey-50"
          />
        )}
        {!isEmpty(port.carriers) && (
          <TableBlock
            id="port-carriers"
            heading="Carriers that service this port"
            body={`<p>There are ${port.carriers?.length} carriers that service ${portDetails.name}. We have ranked them based on their scheduled frequency into that Port and included alternative names where available.</p>`}
            rows={port.carriers || []}
            loadMore={8}
            columns={[
              { key: 'name', name: 'Carrier Name', icon: { component: 'carrierCode', type: portType } },
              { key: 'frequency', name: 'Departure frequency' },
              { key: 'otp', name: 'On time arrivals (Last month)' },
              { key: 'link', name: '' },
            ]}
          />
        )}

        {port.seaportInfo && (
          <FeaturesStatusBlock
            id="seaport-info"
            data={port.seaportInfo}
            heading="Port features &amp; facilities"
            noTopPadding
          />
        )}

        {shouldShowPortRanking && (
          <GridBlock id="ranking-list" heading="Port statistics" columns="three" noTopPadding>
            <div>
              {!isEmpty(port.globalRankings) && (
                <RankingList
                  title="Global Rank"
                  description={`${port.details.name} is ranked ${getOrdinalSuffix(find(port.globalRankings, { id: port.nodeId })?.ranking || 0)} in the world in our <b>Port Connectivity Ranking</b> system which ranks Airports and Seaports by their direct connections to other global ports.`}
                  data={{
                    globalRankings: port.globalRankings || [],
                    regionalRankings: port.countryRankings || [],
                  }}
                  highlightedItemId={port.nodeId || ''}
                  highlightedItemRank={find(port.globalRankings, { id: port.nodeId })?.ranking || 0}
                />
              )}
            </div>
            {!isEmpty(transformedArrivalOnTimePerformance) && (
              <>
                <MiniChartCard
                  data={transformedArrivalOnTimePerformance}
                  id="port-otp"
                  heading="On time performance"
                  body={`This chart displays the On Time Arrival performance for ${port.details.name} as measured by comparing all scheduled vessel arrivals vs actual arrivals.`}
                  chartHeading={`${latestMonthText} OTP`}
                  chartHeadingHighlight={`${lastValue}%`}
                  chartSubHeading={`Previous month OTP ${secondToLastValue}%`}
                  badgeText={`${difference}%`}
                  badgeType={hasIncreased ? 'positive' : 'negative'}
                  chartConfig={{
                    [RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY]: {
                      label: 'Schedule Reliability',
                      color: colors.charts.line[0],
                    },
                  }}
                  chartValueSuffix="%"
                />
                <MiniChartCard
                  data={transformedAverageDaysLate}
                  id="port-average-days-late"
                  heading="Average days late"
                  body={`A vessel is considered late when arriving more than 1 day behind schedule. This chart shows the average number of days that vessels are typically late arriving into ${port.details.name}.`}
                  chartHeading={averageDaysLatestMonthText}
                  chartHeadingHighlight={`${averageDaysLastValue} ${pluralize('day', Number(averageDaysLastValue))}`}
                  chartSubHeading={`Previous month ${averageDaysSecondToLastValue} ${pluralize('day', Number(averageDaysSecondToLastValue))}`}
                  badgeText={`${averageDaysDifference} ${pluralize('day', Number(averageDaysDifference))}`}
                  badgeType={averageDaysHasIncreased ? 'negative' : 'positive'}
                  badgeArrowDirection={averageDaysHasIncreased ? 'up' : 'down'}
                  isLocked={false}
                  chartConfig={{
                    [RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE]: {
                      label: 'Average days late',
                      color: colors.charts.line[0],
                    },
                  }}
                />
              </>
            )}
          </GridBlock>
        )}
        <AccordionBlock
          id="faqs"
          heading={`Frequently asked questions about ${portDetails.name}`}
          items={port.faqs}
          className="bg-grey-100"
        />
      </div>
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ params = {} }) => {
  const { slug } = params;
  // Extract id and type from slug
  const [portCodeType, portId] = slug || [];

  const [portData, posts] = await Promise.all([
    portService.getById(portId, portCodeType as IdentifierApiType),
    cmsPostService.getPostSnippetsCached(),
  ]);

  // Return "not found" if no port details
  if (!portData || !portData.forId || isEmpty(portData.carriers)) {
    return {
      notFound: true,
    };
  }

  // Transform port data
  const transformedPortData = transformPortData(portData);
  const portDetails = transformedPortData?.details;

  // SEO - Generate page titles, descriptions etc
  const canonical = createPortPageUrl(portDetails?.identifiers, true);
  const seoMetaTags = createMetaTagsWithCustomTitleAndDescription({
    title: portDetails ? formatPortTitle(portDetails) : '',
    description: portDetails?.description || '',
  });

  // Generate FAQs for page
  const faqs = transformedPortData ? generatePortFAQs(transformedPortData) : null;

  const lastSixMonthsArrivalOnTimePerformance = getLastXMonths({
    data: portData.reliability.arrivalOnTimePerformance,
    months: 6,
  });

  return {
    props: {
      port: {
        ...transformedPortData,
        canonical,
        seoMetaTags,
        faqs,
        reliability: {
          arrivalOnTimePerformance: lastSixMonthsArrivalOnTimePerformance,
        },
      },
      posts,
    },
    revalidate: PAGE_REVALIDATE_INTERVAL_MONTH,
  };
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: true,
});

export default Page;
